/** Meetings list page: filters and search work against the live list, with honest empty states. */
import { $, $$, byId, esc, on } from "../lib/dom";
import { fmtShortDate } from "../lib/time";
import type { Meeting } from "../data/types";
import { state } from "../state";

type Filter = "all" | "shared" | "processing";
let filter: Filter = "all";
/** Lower-cased project name, or "" for every project. One at a time: this is a quick sift, not a query builder. */
let project = "";
let search = "";
let latest: Meeting[] = [];

export const projectsOf = (m: Meeting) => (m.projects ?? []).filter(Boolean);

/** Every project across the list, most used first, so the common ones sit nearest the left. */
function projectCounts(meetings: Meeting[]) {
  const n = new Map<string, { name: string; count: number }>();
  for (const m of meetings) for (const p of projectsOf(m)) {
    const k = p.toLowerCase();
    const e = n.get(k) ?? { name: p, count: 0 };
    e.count += 1;
    n.set(k, e);
  }
  return [...n.entries()].sort((a, b) => b[1].count - a[1].count || a[1].name.localeCompare(b[1].name));
}

const haystack = (m: Meeting) => [m.title, m.summary, m.summarySoFar, ...(m.projects ?? []), ...(m.keyPoints ?? []), ...(m.decisions ?? []).map((d) => d.text), ...(m.entities ?? []).map((e) => e.name)].filter(Boolean).join(" ").toLowerCase();
const matches = (m: Meeting) => {
  if (project && !projectsOf(m).some((p) => p.toLowerCase() === project)) return false;
  if (filter === "shared" && !m.shareId) return false;
  if (filter === "processing" && m.status !== "processing" && m.status !== "recording" && m.status !== "paused") return false;
  return !search || haystack(m).includes(search);
};

export function renderMeetings(meetings: Meeting[]) {
  const list = $("#page-meetings .list-page");
  if (!list) return;
  latest = meetings;
  const month = meetings.filter((m) => m.startedAt.getMonth() === new Date().getMonth()).length;
  $("#page-meetings .eyebrow")!.textContent = `Meeting memory · ${month} this month`;
  const hours = meetings.reduce((a, m) => a + m.durationSec, 0) / 3600;
  const actions = meetings.reduce((a, m) => a + (m.actionItems?.length ?? 0), 0);
  const decisions = meetings.reduce((a, m) => a + (m.decisions?.length ?? 0), 0);
  const withOwner = meetings.reduce((a, m) => a + (m.actionItems?.filter((x) => x.owner).length ?? 0), 0);
  const metrics = $("#page-meetings .metric-grid")!;
  metrics.innerHTML = [
    ["Captured", String(meetings.length), `${hours.toFixed(1)}h of searchable context`],
    ["Actions found", String(actions), actions ? `${Math.round((withOwner / actions) * 100)}% have an owner` : "Record a meeting to start"],
    ["Decisions", String(decisions), "Linked to notes and tasks"],
    ["Processing", String(meetings.filter((m) => m.status === "processing").length), "Summaries in progress"],
  ].map(([k, v, t]) => `<div class="card metric-card"><small>${k}</small><div class="metric-value">${v}</div><div class="metric-trend">${t}</div></div>`).join("");

  const shown = meetings.filter(matches);
  $$<HTMLElement>("[data-meeting-filter]").forEach((b) => {
    const f = b.dataset.meetingFilter as Filter;
    b.classList.toggle("active", f === filter);
    const n = f === "all" ? meetings.length : meetings.filter((m) => (f === "shared" ? !!m.shareId : m.status === "processing" || m.status === "recording" || m.status === "paused")).length;
    b.innerHTML = `${f === "all" ? "All" : f === "shared" ? "Shared" : "Processing"}${n ? ` <span class="seg-count">${n}</span>` : ""}`;
  });

  const chips = byId("meetingProjects");
  if (chips) {
    const counts = projectCounts(meetings);
    // Nothing tagged yet means nothing to sift; an empty row of controls is just noise.
    chips.hidden = counts.length === 0;
    chips.innerHTML = counts.length
      ? `<button class="filter-chip${project ? "" : " active"}" data-project="">All projects</button>` +
        counts.map(([key, v]) => `<button class="filter-chip${project === key ? " active" : ""}" data-project="${esc(key)}">${esc(v.name)} <span class="seg-count">${v.count}</span></button>`).join("")
      : "";
  }

  if (!state.loaded.meetings) { list.innerHTML = `<p class="meta loading-line"><span class="loading-dot"></span>Loading your meetings…</p>`; return; }
  list.innerHTML = shown.length
    ? shown.map((m) => {
        const live = m.status === "recording" || m.status === "paused";
        const meta = live
          ? `Recording now · ${m.chunkCount} segments captured`
          : m.status === "processing"
            ? `${Math.round(m.durationSec / 60)} min · Timo is finishing the summary`
            : m.status === "failed"
              ? `${Math.round(m.durationSec / 60)} min · Summary failed. Transcript saved`
              : `${Math.round(m.durationSec / 60)} min · ${m.actionItems?.length ?? 0} actions · ${m.decisions?.length ?? 0} decisions`;
        return `<div class="card meeting-row" data-meeting-id="${m.id}">
          <div class="date-tile${live ? " live" : ""}">${live
            ? `<svg class="ui-icon" viewBox="0 0 24 24"><rect x="8" y="3" width="8" height="12" rx="4"></rect><path d="M5 11a7 7 0 0 0 14 0"></path></svg><small>Live</small>`
            : `<b>${m.startedAt.getDate()}</b><small>${m.startedAt.toLocaleDateString(undefined, { month: "short" })}</small>`}</div>
          <div class="meeting-copy"><b>${esc(m.title)}</b><div class="meta">${meta}</div>${projectsOf(m).length ? `<div class="row-tags">${projectsOf(m).map((p) => `<span class="tag">${esc(p)}</span>`).join("")}</div>` : ""}</div>
          <div class="meeting-row-actions">
            ${m.status === "complete" ? `<button class="icon-action" data-share-meeting="${m.id}" title="Share these notes" aria-label="Share these notes"><svg class="ui-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 16V4M8 8l4-4 4 4"/><path d="M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4"/></svg></button>
            <button class="icon-action" data-pdf-meeting="${m.id}" title="Download PDF" aria-label="Download PDF"><svg class="ui-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M7 3h7l5 5v13H7z"/><path d="M14 3v5h5"/><path d="M12 11v6m0 0-2.5-2.5M12 17l2.5-2.5"/></svg></button>` : ""}
            <button class="btn" data-open-meeting="${m.id}">${live ? "Open live →" : "Open →"}</button>
          </div>
        </div>`;
      }).join("")
    : `<div class="card meeting-row empty-row"><div class="date-tile"><b>–</b><small>${meetings.length ? "None" : "Now"}</small></div><div class="meeting-copy"><b>${search ? "Nothing matches that search" : project ? "Nothing in that project yet" : filter === "shared" ? "No shared meetings yet" : filter === "processing" ? "Nothing is processing" : "No meetings yet"}</b><div class="meta">${search ? "Try a decision, a person, or part of the title." : filter === "shared" ? "Share a meeting\u2019s notes and it appears here." : filter === "processing" ? "Recordings being transcribed and summarized show up here." : "Press \u201cRecord meeting\u201d to capture your first one. Timo transcribes as you go."}</div></div></div>`;
}


export function setupMeetings(getMeetings: () => Meeting[]) {
  $$<HTMLElement>("[data-meeting-filter]").forEach((b) => on(b, "click", () => { filter = (b.dataset.meetingFilter as Filter) ?? "all"; renderMeetings(getMeetings()); }));
  on(byId("meetingSearch"), "input", (e) => { search = (e.target as HTMLInputElement).value.trim().toLowerCase(); renderMeetings(getMeetings()); });
  // Delegated: the chips are rebuilt on every render, so binding them individually would not survive.
  on(byId("meetingProjects"), "click", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>("[data-project]");
    if (!b) return;
    const next = b.dataset.project ?? "";
    project = next === project ? "" : next;      // tapping the active chip clears it
    renderMeetings(getMeetings());
  });
}
